/**
 * Elite Decision Engine
 * Revenue-driven prioritization engine for Decision Sales OS
 * 
 * Generates a Today list with:
 * - Priority ordering: HOT overdue > warm overdue > HOT SLA > etc
 * - SLA violation detection (hot=6h, warm=24h, cold=72h)
 * - Postponed deal detection
 * - Execution debt calculation
 * - Contact actions (phone, WhatsApp, email)
 * 
 * AUDIT FIX (2025-01-16):
 * - Fixed: Filters out archived leads (archived = true, boolean field)
 * - Schema: leads.archived (boolean), NOT leads.archived_at
 */

import { Lead } from '@/hooks/useLeads';
import { Deal } from '@/hooks/useDeals';
import { differenceInHours, differenceInDays, isAfter, isBefore, startOfDay, addDays, format } from 'date-fns';
import { computeInaction } from '@/lib/inactionEngine';

// ============================================================================
// TYPES
// ============================================================================

export interface ActionContact {
  phone?: string;
  whatsappLink?: string;
  telLink?: string;
  email?: string;
}

export type ActionType = 
  | 'CONTACT_NOW' 
  | 'FOLLOW_UP' 
  | 'RESCHEDULE_CONFIRM' 
  | 'QUALIFY_BANT' 
  | 'CLOSE_PUSH'
  | 'SET_CLOSE_DATE'
  | 'SET_NEXT_STEP'
  | 'ADD_CONTACT' // For leads without contact details (phone/email)
  | 'ARCHIVE';

export interface EliteAction {
  id: string;
  leadId: string;
  dealId?: string;
  lead: Lead;
  deal?: Deal;
  actionType: ActionType;
  dueAt: Date;
  reasons: string[];
  moneyAtRisk: number;
  priorityScore: number;
  estimatedMinutes: number;
  contact?: ActionContact;
  recommendedScript: string[];
  isPostponed?: boolean;
  isMandatory: boolean;
  // Priority system (0-100)
  priority: number;
  priorityReasons: Array<{ reason: string; weight: number }>;
  hasFutureFollowUp?: boolean; // If follow-up scheduled in future, reduce visual tension
}

export interface EliteTodayPlan {
  oneThing: EliteAction | null;
  mandatory: EliteAction[];
  optional: EliteAction[];
  tomorrow: EliteAction[];
  insights: {
    executionDebt: {
      debtPoints: number;
      debtMoney: number;
    };
    dailyCapacity: {
      mandatoryCount: number;
      optionalCount: number;
      backlogCount: number;
    };
    pipelineHygiene: {
      missingCloseDates: number;
      missingNextSteps: number;
    };
    temperatureDrift: {
      hotToWarm: number;
    };
  };
}

export interface EliteEngineInput {
  leads: Lead[];
  deals: Deal[];
  config?: {
    maxColdPerDay?: number;
    staleDealDays?: number;
    mandatoryLimit?: number;
    optionalLimit?: number;
    enableTemperatureDrift?: boolean;
  };
}

// ============================================================================
// CONFIG
// ============================================================================

const DEFAULT_CONFIG = {
  maxColdPerDay: 3,
  staleDealDays: 5,
  mandatoryLimit: 12,
  optionalLimit: 18,
  enableTemperatureDrift: false,
};

const SLA_HOURS = {
  hot: 6,
  warm: 24,
  cold: 72,
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Sanitize phone number to E.164 format
 */
export function sanitizePhoneE164(phone: string | null | undefined): string | null {
  if (!phone) return null;
  // Remove spaces, dashes, parentheses, keep + and numbers
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  // Ensure starts with + or add country code (default +39 for Italy)
  if (cleaned.startsWith('+')) {
    return cleaned;
  }
  // If starts with 0, replace with +39
  if (cleaned.startsWith('0')) {
    return '+39' + cleaned.substring(1);
  }
  // If starts with number, add +39
  if (/^\d/.test(cleaned)) {
    return '+39' + cleaned;
  }
  return null;
}

/**
 * Build WhatsApp link (E.164 without +)
 */
export function buildWhatsAppLink(phone: string | null | undefined, message?: string): string | null {
  const e164 = sanitizePhoneE164(phone);
  if (!e164) return null;
  const baseUrl = `https://wa.me/${e164.replace('+', '')}`;
  if (message) {
    return `${baseUrl}?text=${encodeURIComponent(message)}`;
  }
  return baseUrl;
}

/**
 * Build tel: link
 */
export function buildTelLink(phone: string | null | undefined): string | null {
  const e164 = sanitizePhoneE164(phone);
  if (!e164) return null;
  return `tel:${e164}`;
}

/**
 * Build contact payload for an action
 */
export function buildContactAction(lead: Lead): ActionContact {
  const phone = lead.phone || null;
  return {
    phone: phone || undefined,
    whatsappLink: buildWhatsAppLink(phone) || undefined,
    telLink: buildTelLink(phone) || undefined,
    email: lead.email || undefined,
  };
}

/**
 * Check if SLA is violated
 */
export function isSLAViolated(
  lead: Lead,
  lastContactAt: Date | null,
  now: Date = new Date()
): boolean {
  if (!lastContactAt) return true; // Never contacted = SLA violated
  
  const temperature = lead.temperature || 'cold';
  const slaHours = SLA_HOURS[temperature] || SLA_HOURS.cold;
  const hoursSinceContact = differenceInHours(now, lastContactAt);
  
  return hoursSinceContact > slaHours;
}

/**
 * Check if follow-up is overdue
 */
export function isOverdue(
  nextFollowUpAt: Date | null,
  now: Date = new Date()
): boolean {
  if (!nextFollowUpAt) return false;
  return isBefore(nextFollowUpAt, now);
}

/**
 * Check if deal is stale
 */
export function isStaleDeal(
  lastContactAt: Date | null,
  staleThresholdDays: number = DEFAULT_CONFIG.staleDealDays,
  now: Date = new Date()
): boolean {
  if (!lastContactAt) return false;
  const daysSinceContact = differenceInDays(now, lastContactAt);
  return daysSinceContact > staleThresholdDays;
}

/**
 * Compute Priority score (0-100) for decision engine - DSOS CONTACT-FIRST + URGENCY
 * 
 * PRIORITY SCORE with clear weights:
 * - +35 if overdueFollowup
 * - +25 if neverContacted AND actionable
 * - +20 if stale (>= 5 days)
 * - +15 if lateStage (proposal/negotiation)
 * - +0..+20 based on dealValue (logarithmic scale)
 * - -30 if NOT actionable (no contact) -> but still generates "Complete contact details" action
 * - -10 if has only email (less immediate than call/whatsapp)
 */
export function computePriority(
  lead: Lead,
  deal: Deal | undefined,
  now: Date = new Date()
): { priority: number; reasons: Array<{ reason: string; weight: number }> } {
  let priority = 0;
  const priorityReasons: Array<{ reason: string; weight: number }> = [];
  
  const lastContactAt = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
  const nextFollowUpAt = (lead as any).next_follow_up_at 
    ? new Date((lead as any).next_follow_up_at) 
    : (lead.next_action_at ? new Date(lead.next_action_at) : null);
  const dealValue = deal?.value || 0;
  const dealStage = deal?.stage || null;
  const leadStage = lead.pipeline_stage || 'new';
  
  // ============================================================================
  // 1. COMPUTE ACTIONABILITY (contact-first logic)
  // ============================================================================
  const phone = lead.phone || null;
  const email = lead.email || null;
  const hasPhone = !!phone && phone.trim().length > 0;
  const hasWhatsapp = hasPhone; // If has phone, can use WhatsApp
  const hasEmail = !!email && email.trim().length > 0 && email.includes('@');
  const actionable = hasPhone || hasWhatsapp || hasEmail;
  const hasOnlyEmail = hasEmail && !hasPhone; // Only email, no phone
  
  // ============================================================================
  // 2. DETERMINE URGENCY FACTORS
  // ============================================================================
  const overdueFollowup = isOverdue(nextFollowUpAt, now);
  const neverContacted = !lastContactAt;
  // Calculate stale days only if we have a last contact date
  // Never use 999 as placeholder - if no date, stale is false
  const staleDays = lastContactAt ? differenceInDays(now, lastContactAt) : null;
  const stale = staleDays !== null && staleDays >= 5; // Configurable: 5 days = stale
  const lateStage = dealStage && ['proposal_sent', 'negotiation'].includes(dealStage) || 
                    leadStage && ['proposal', 'negotiation'].includes(leadStage);
  
  // ============================================================================
  // 3. PRIORITY SCORE CALCULATION (DSOS weights)
  // ============================================================================
  
  // URGENCY FACTORS (highest priority)
  if (overdueFollowup) {
    priority += 35;
    priorityReasons.push({ reason: 'Overdue follow-up', weight: 35 });
  }
  
  if (neverContacted && actionable) {
    priority += 25;
    priorityReasons.push({ reason: 'Never contacted', weight: 25 });
  }
  
  if (stale && staleDays !== null) {
    priority += 20;
    priorityReasons.push({ reason: `Stale (${staleDays} days)`, weight: 20 });
  }
  
  if (lateStage) {
    priority += 15;
    priorityReasons.push({ reason: 'Late stage', weight: 15 });
  }
  
  // DEAL VALUE BOOST (logarithmic scale: 0-20 points)
  if (dealValue > 0) {
    // Logarithmic scale: 1K = +5, 5K = +10, 10K = +15, 20K+ = +20
    const valueScore = Math.min(20, Math.floor(Math.log10(dealValue / 1000 + 1) * 10));
    if (valueScore > 0) {
      priority += valueScore;
      priorityReasons.push({ reason: `€${(dealValue / 1000).toFixed(0)}K deal`, weight: valueScore });
    }
  }
  
  // ACTIONABILITY PENALTIES (contact-first: penalize if not actionable)
  if (!actionable) {
    priority -= 30;
    priorityReasons.push({ reason: 'No contact details', weight: -30 });
  } else if (hasOnlyEmail) {
    priority -= 10;
    priorityReasons.push({ reason: 'Email only (less immediate)', weight: -10 });
  }
  
  // Clamp to 0-100
  priority = Math.min(100, Math.max(0, priority));
  
  // Sort reasons by weight (descending)
  priorityReasons.sort((a, b) => b.weight - a.weight);
  
  return { priority, reasons: priorityReasons };
}

/**
 * Compute lead priority score (0-100) - LEGACY (kept for backward compatibility)
 */
export function computeLeadPriority(
  lead: Lead,
  deal: Deal | undefined,
  now: Date = new Date()
): { score: number; reasons: string[] } {
  let score = 0;
  const reasons: string[] = [];
  
  const temperature = lead.temperature || 'cold';
  const lastContactAt = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
  const nextFollowUpAt = (lead as any).next_follow_up_at 
    ? new Date((lead as any).next_follow_up_at) 
    : (lead.next_action_at ? new Date(lead.next_action_at) : null);
  
  const dealValue = deal?.value || 0;
  const dealStage = deal?.stage || null;
  
  // 0. HIGH VALUE DEALS - PRIORITY SCALES WITH VALUE
  // The higher the deal value, the higher the priority (proportional)
  if (dealValue >= 5000) {
    // Base score scales with deal value: 5K = 85, 10K = 90, 20K = 95, 50K+ = 100
    // Formula: min(100, 80 + (dealValue / 1000) * 0.5)
    let highValueScore = Math.min(100, 80 + (dealValue / 1000) * 0.5);
    
    // Additional boost based on urgency (adds to base score)
    if (isOverdue(nextFollowUpAt, now)) {
      highValueScore = Math.min(100, highValueScore + 10); // +10 for overdue
      reasons.push(`€${(dealValue / 1000).toFixed(0)}K deal with overdue follow-up - CRITICAL`);
      return { score: highValueScore, reasons };
    }
    
    if (isSLAViolated(lead, lastContactAt, now)) {
      highValueScore = Math.min(100, highValueScore + 8); // +8 for SLA violation
      reasons.push(`€${(dealValue / 1000).toFixed(0)}K deal - SLA violated - URGENT`);
      return { score: highValueScore, reasons };
    }
    
    // High value deals are always top priority (proportional to value)
    reasons.push(`€${(dealValue / 1000).toFixed(0)}K deal - HIGH VALUE PRIORITY`);
    return { score: highValueScore, reasons };
  }
  
  // 1. HOT with overdue follow-up (highest priority for smaller deals)
  if (temperature === 'hot' && isOverdue(nextFollowUpAt, now)) {
    score += 100;
    reasons.push('HOT lead with overdue follow-up');
    return { score, reasons };
  }
  
  // 2. HOT with SLA violated
  if (temperature === 'hot' && isSLAViolated(lead, lastContactAt, now)) {
    score += 90;
    reasons.push('HOT lead - SLA violated (no contact in 6h)');
    return { score, reasons };
  }
  
  // 3. Warm with overdue follow-up
  if (temperature === 'warm' && isOverdue(nextFollowUpAt, now)) {
    score += 80;
    reasons.push('Warm lead with overdue follow-up');
    return { score, reasons };
  }
  
  // 4. Deal in Proposal/Negotiation with high amount and no next step
  if (dealStage && ['proposal_sent', 'negotiation'].includes(dealStage) && dealValue > 5000) {
    if (!nextFollowUpAt) {
      score += 75;
      reasons.push('High-value deal in proposal/negotiation - no next step set');
      return { score, reasons };
    }
  }
  
  // 5. Warm with SLA violated
  if (temperature === 'warm' && isSLAViolated(lead, lastContactAt, now)) {
    score += 70;
    reasons.push('Warm lead - SLA violated (no contact in 24h)');
    return { score, reasons };
  }
  
  // 6. HOT never contacted (anti-churn rule)
  if (temperature === 'hot' && !lastContactAt) {
    score += 85;
    reasons.push('HOT lead never contacted - top priority');
    return { score, reasons };
  }
  
  // 7. Deal value boost (MORE WEIGHT for higher values)
  // Note: 10K+ deals are handled above with early return, so this is for <10K
  if (dealValue > 0 && dealValue < 10000) {
    if (dealValue >= 5000) {
      score += 45; // Increased from 30 - ensures 5000€ > 2300€
      reasons.push(`€${(dealValue / 1000).toFixed(0)}K deal value`);
    } else if (dealValue >= 3000) {
      score += 35; // New tier for 3000-4999
      reasons.push(`€${(dealValue / 1000).toFixed(0)}K deal value`);
    } else if (dealValue >= 1000) {
      score += 25; // Increased from 20
      reasons.push(`€${(dealValue / 1000).toFixed(0)}K deal value`);
    } else {
      score += 10; // Small boost for any deal
    }
  }
  
  // 8. Pipeline stage boost
  const stage = lead.pipeline_stage || 'new';
  if (stage === 'proposal') {
    score += 25;
    reasons.push('Proposal stage - ready to close');
  } else if (stage === 'appointment') {
    score += 15;
    reasons.push('Appointment set');
  } else if (stage === 'contacted') {
    score += 10;
  }
  
  // 9. Staleness penalty
  if (lastContactAt) {
    const daysSinceContact = differenceInDays(now, lastContactAt);
    if (daysSinceContact > 7 && temperature === 'hot') {
      score += 15;
      reasons.push('Hot lead inactive 7+ days');
    } else if (daysSinceContact > 14) {
      score += 10;
      reasons.push('No contact in 2+ weeks');
    }
  }
  
  // 10. Cold leads (limited)
  if (temperature === 'cold') {
    if (dealValue >= 10000) {
      score += 30;
      reasons.push('Cold lead with high-value deal');
    } else {
      score += 5; // Low priority
      reasons.push('Cold lead');
    }
  }
  
  return { score: Math.min(100, Math.max(0, score)), reasons };
}

/**
 * Compute deal risk ratio (0-1)
 */
export function computeDealRisk(
  lead: Lead,
  deal: Deal | undefined,
  now: Date = new Date()
): number {
  if (!deal || !deal.value) return 0;
  
  let riskRatio = 0.1; // Base risk
  
  const lastContactAt = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
  const nextFollowUpAt = (lead as any).next_follow_up_at 
    ? new Date((lead as any).next_follow_up_at) 
    : (lead.next_action_at ? new Date(lead.next_action_at) : null);
  
  // Overdue follow-up increases risk
  if (isOverdue(nextFollowUpAt, now)) {
    riskRatio += 0.3;
  }
  
  // SLA violation increases risk
  if (isSLAViolated(lead, lastContactAt, now)) {
    riskRatio += 0.2;
  }
  
  // Stale deal increases risk
  if (isStaleDeal(lastContactAt, DEFAULT_CONFIG.staleDealDays, now)) {
    riskRatio += 0.2;
  }
  
  // Stage-based risk
  const stage = deal.stage || 'new';
  if (['proposal_sent', 'negotiation'].includes(stage)) {
    riskRatio += 0.1; // Higher risk if in closing stages
  }
  
  return Math.min(1, riskRatio);
}

/**
 * Detect postponed deals
 */
export function detectPostponedDeals(
  lead: Lead,
  deal: Deal | undefined,
  now: Date = new Date()
): boolean {
  if (!deal) return false;
  
  const nextFollowUpAt = (lead as any).next_follow_up_at 
    ? new Date((lead as any).next_follow_up_at) 
    : (lead.next_action_at ? new Date(lead.next_action_at) : null);
  
  if (!nextFollowUpAt) return false;
  
  const tomorrow = startOfDay(addDays(now, 1));
  const isFuture = isAfter(nextFollowUpAt, now);
  const isTomorrowOrLater = isAfter(nextFollowUpAt, tomorrow) || 
    format(nextFollowUpAt, 'yyyy-MM-dd') === format(tomorrow, 'yyyy-MM-dd');
  
  if (!isFuture || !isTomorrowOrLater) return false;
  
  const temperature = lead.temperature || 'cold';
  const dealStage = deal.stage || 'new';
  const dealValue = deal.value || 0;
  
  // Postponed if:
  // - HOT or stage >= proposal and amount > threshold
  // - AND last contact is old (>= 2 days) OR no next step note
  const lastContactAt = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
  // Never use 999 as placeholder - if no date, isOldContact is true
  const daysSinceContact = lastContactAt ? differenceInDays(now, lastContactAt) : null;
  const hasNextStepNote = (lead as any).notes_long || (lead as any).notes_short;
  
  const isHighPriority = (temperature === 'hot' || 
    ['proposal_sent', 'negotiation'].includes(dealStage)) && 
    dealValue > 3000;
  
  const isOldContact = (daysSinceContact !== null && daysSinceContact >= 2) || !lastContactAt;
  const noNextStepNote = !hasNextStepNote;
  
  return isHighPriority && (isOldContact || noNextStepNote);
}

/**
 * Determine action type
 */
export function determineActionType(
  lead: Lead,
  deal: Deal | undefined,
  now: Date = new Date()
): ActionType {
  const lastContactAt = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
  const nextFollowUpAt = (lead as any).next_follow_up_at 
    ? new Date((lead as any).next_follow_up_at) 
    : (lead.next_action_at ? new Date(lead.next_action_at) : null);
  
  const dealStage = deal?.stage || null;
  const stage = lead.pipeline_stage || 'new';
  
  // Postponed detection
  if (detectPostponedDeals(lead, deal, now)) {
    return 'RESCHEDULE_CONFIRM';
  }
  
  // Never contacted
  if (!lastContactAt) {
    return 'CONTACT_NOW';
  }
  
  // Overdue follow-up
  if (isOverdue(nextFollowUpAt, now)) {
    return 'FOLLOW_UP';
  }
  
  // SLA violated
  if (isSLAViolated(lead, lastContactAt, now)) {
    return 'CONTACT_NOW';
  }
  
  // Close push for negotiation stage
  if (dealStage === 'negotiation' || stage === 'proposal') {
    return 'CLOSE_PUSH';
  }
  
  // Missing next step
  if (!nextFollowUpAt && (stage === 'contacted' || stage === 'appointment')) {
    return 'SET_NEXT_STEP';
  }
  
  // Missing close date for proposal/negotiation
  if (dealStage && ['proposal_sent', 'negotiation'].includes(dealStage)) {
    const expectedCloseDate = deal.expected_close_date;
    if (!expectedCloseDate) {
      return 'SET_CLOSE_DATE';
    }
  }
  
  // Qualify BANT for early stages
  if (stage === 'new' || stage === 'contacted') {
    return 'QUALIFY_BANT';
  }
  
  return 'FOLLOW_UP';
}

/**
 * Generate recommended script (3 bullets)
 */
export function generateRecommendedScript(
  actionType: ActionType,
  lead: Lead,
  deal: Deal | undefined
): string[] {
  const name = lead.name;
  const company = lead.company || 'your company';
  
  switch (actionType) {
    case 'ADD_CONTACT':
      return [
        `Complete contact details for ${name}`,
        `Add phone number or email to enable direct contact`,
        `This lead needs contact information to proceed`
      ];
    
    case 'CONTACT_NOW':
      if (!lead.last_interaction_at) {
        return [
          `Hi ${name}, I'm reaching out from ${company}`,
          `I'd love to understand your needs and see how we can help`,
          `Do you have 10 minutes for a quick call this week?`
        ];
      }
      return [
        `Hi ${name}, following up on our previous conversation`,
        `I wanted to check in and see if you have any questions`,
        `Are you still interested in moving forward?`
      ];
    
    case 'FOLLOW_UP':
      return [
        `Hi ${name}, I'm following up as promised`,
        `I wanted to see where we are in the process`,
        `What's the best time to reconnect?`
      ];
    
    case 'RESCHEDULE_CONFIRM':
      return [
        `Hi ${name}, I see we have something scheduled for tomorrow`,
        `I wanted to confirm the time and make sure it still works for you`,
        `Can we lock in the details?`
      ];
    
    case 'CLOSE_PUSH':
      return [
        `Hi ${name}, we're close to finalizing everything`,
        `I wanted to address any final concerns or questions`,
        `What do we need to move forward and close this week?`
      ];
    
    case 'QUALIFY_BANT':
      return [
        `Hi ${name}, I'd love to understand your situation better`,
        `Do you have Budget, Authority, Need, and Timeline (BANT) in place?`,
        `Let's schedule a call to discuss your requirements`
      ];
    
    default:
      return [
        `Hi ${name}, I wanted to touch base`,
        `I'd like to discuss how we can help you`,
        `What's the best time to connect?`
      ];
  }
}

/**
 * Build elite action from lead/deal
 */
export function buildEliteAction(
  lead: Lead,
  deal: Deal | undefined,
  now: Date = new Date()
): EliteAction {
  const { score, reasons } = computeLeadPriority(lead, deal, now);
  const { priority, reasons: priorityReasons } = computePriority(lead, deal, now);
  const riskRatio = computeDealRisk(lead, deal, now);
  const dealValue = deal?.value || 0;
  const moneyAtRisk = dealValue * riskRatio;
  const actionType = determineActionType(lead, deal, now);
  const isPostponed = detectPostponedDeals(lead, deal, now);
  
  const nextFollowUpAt = (lead as any).next_follow_up_at 
    ? new Date((lead as any).next_follow_up_at) 
    : (lead.next_action_at ? new Date(lead.next_action_at) : null);
  
  // Check if follow-up is scheduled in future (reduces visual tension)
  const hasFutureFollowUp = nextFollowUpAt && isAfter(nextFollowUpAt, now);
  
  const dueAt = nextFollowUpAt || now;
  const estimatedMinutes = actionType === 'CLOSE_PUSH' ? 20 : 
    actionType === 'QUALIFY_BANT' ? 15 : 10;
  
  const recommendedScript = generateRecommendedScript(actionType, lead, deal);
  const contact = buildContactAction(lead);
  
  // Determine if mandatory based on priority score and action type
  // 5K+ deals are ALWAYS mandatory (proportional: higher value = always mandatory)
  const isMandatory = dealValue >= 5000 ||
    score >= 70 || 
    actionType === 'CONTACT_NOW' || 
    actionType === 'FOLLOW_UP' ||
    isPostponed;
  
  return {
    id: `elite-${lead.id}-${deal?.id || 'no-deal'}`,
    leadId: lead.id,
    dealId: deal?.id,
    lead,
    deal,
    actionType,
    dueAt,
    reasons,
    moneyAtRisk,
    priorityScore: score,
    estimatedMinutes,
    contact,
    recommendedScript,
    isPostponed,
    isMandatory,
    priority,
    priorityReasons,
    hasFutureFollowUp,
  };
}

// ============================================================================
// MAIN ENGINE
// ============================================================================

/**
 * Build Elite Today Plan
 */
export function buildEliteTodayPlan(input: EliteEngineInput): EliteTodayPlan {
  const { leads, deals, config = {} } = input;
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const now = new Date();
  const today = startOfDay(now);
  const tomorrow = startOfDay(addDays(now, 1));
  
  // Filter active leads/deals
  // CRITICAL: Home = today. Exclude leads with scheduled follow-up in future.
  const activeLeads = leads.filter(l => {
    // Exclude archived leads (archived is boolean, NOT archived_at)
    if ((l as any).archived === true) {
      return false;
    }
    
    // Exclude won/lost/closed
    if (l.pipeline_stage === 'won' || l.pipeline_stage === 'lost' || (l as any).closed_at) {
      return false;
    }
    
    // Exclude leads with next_follow_up_at in future (deferred)
    const nextFollowUpAt = (l as any).next_follow_up_at 
      ? new Date((l as any).next_follow_up_at) 
      : (l.next_action_at ? new Date(l.next_action_at) : null);
    
    if (nextFollowUpAt && isAfter(nextFollowUpAt, now)) {
      // Follow-up is scheduled for future - exclude from Home
      return false;
    }
    
    return true;
  });
  
  const activeDeals = deals.filter(d => 
    d.stage !== 'won' && 
    d.stage !== 'lost'
  );
  
  // Build lead -> deal map
  const leadDealMap = new Map<string, Deal>();
  activeDeals.forEach(deal => {
    const existing = leadDealMap.get(deal.lead_id);
    if (!existing || (deal.value || 0) > (existing.value || 0)) {
      leadDealMap.set(deal.lead_id, deal);
    }
  });
  
  // Build all actions
  const allActions: EliteAction[] = activeLeads.map(lead => {
    const deal = leadDealMap.get(lead.id);
    return buildEliteAction(lead, deal, now);
  });
  
  // Separate mandatory and optional
  // DSOS PRIORITY-BASED SORTING (Contact-First + Urgency):
  // 1. Sort by priority score (from computePriority) - DESCENDING
  // 2. Within same priority: overdue > never contacted > stale > late stage > deal value
  // 3. Actionable leads (phone/whatsapp) ranked higher than email-only
  // 4. Leads without contact still appear but with lower priority (action: "Complete contact details")
  
  // CRITICAL: If we have active leads, we MUST produce at least one mandatory action
  // This ensures Today always shows Focus + Mandatory Actions when leads exist
  const mandatory = allActions
    .filter(a => {
      // CRITICAL: Show ALL leads with priority > 0 (even if not actionable)
      // This ensures "Complete contact details" actions appear
      // If no actions have priority > 0, include at least the first one (fallback)
      return a.priority > 0;
    })
    .sort((a, b) => {
      // PRIMARY: Priority score (DESCENDING) - from computePriority (DSOS weights)
      if (b.priority !== a.priority) {
        return b.priority - a.priority;
      }
      
      // SECONDARY: Tie-breaker by urgency factors (from priorityReasons)
      const getUrgencyWeight = (reasons: Array<{ reason: string; weight: number }>): number => {
        // Overdue follow-up = highest
        if (reasons.some(r => r.reason === 'Overdue follow-up')) return 100;
        // Never contacted = second
        if (reasons.some(r => r.reason === 'Never contacted')) return 90;
        // Stale = third
        const staleReason = reasons.find(r => r.reason.includes('Stale'));
        if (staleReason) {
          const daysMatch = staleReason.reason.match(/(\d+)/);
          const days = daysMatch ? parseInt(daysMatch[1]) : 5;
          return 80 + days; // More days = higher weight
        }
        // Late stage = fourth
        if (reasons.some(r => r.reason === 'Late stage')) return 70;
        return 0;
      };
      
      const urgencyA = getUrgencyWeight(a.priorityReasons);
      const urgencyB = getUrgencyWeight(b.priorityReasons);
      if (urgencyB !== urgencyA) {
        return urgencyB - urgencyA;
      }
      
      // TERTIARY: Inaction state (OVERDUE > DUE > FRESH > STALE)
      const inactionA = computeInaction({ lead: a.lead });
      const inactionB = computeInaction({ lead: b.lead });
      const inactionOrder: Record<string, number> = { OVERDUE: 4, DUE: 3, FRESH: 2, STALE: 1 };
      const inactionScoreA = inactionOrder[inactionA.state] || 0;
      const inactionScoreB = inactionOrder[inactionB.state] || 0;
      if (inactionScoreB !== inactionScoreA) {
        return inactionScoreB - inactionScoreA;
      }
      
      // QUATERNARY: Actionability (phone/whatsapp > email-only > no contact)
      const actionableA = (a.contact?.phone || a.contact?.whatsappLink) ? 2 : (a.contact?.email ? 1 : 0);
      const actionableB = (b.contact?.phone || b.contact?.whatsappLink) ? 2 : (b.contact?.email ? 1 : 0);
      if (actionableB !== actionableA) {
        return actionableB - actionableA;
      }
      
      // QUINARY: Deal value (tie-breaker)
      return (b.moneyAtRisk || 0) - (a.moneyAtRisk || 0);
    });
  
  // CRITICAL: If no actions have priority > 0 but we have active leads, include at least the first one
  // This ensures Today always shows Focus + Mandatory Actions when leads exist
  const finalMandatory = mandatory.length > 0
    ? mandatory.slice(0, finalConfig.mandatoryLimit || 12)
    : (allActions.length > 0 ? [allActions[0]] : []); // Fallback: at least first action if leads exist
  
  const optional = allActions
    .filter(a => {
      // Optional: priority > 0 but not in mandatory (or lower priority)
      return a.priority > 0 && !mandatory.some(m => m.id === a.id);
    })
    .sort((a, b) => {
      // Same sorting logic as mandatory
      if (b.priority !== a.priority) {
        return b.priority - a.priority;
      }
      
      const getUrgencyWeight = (reasons: Array<{ reason: string; weight: number }>): number => {
        if (reasons.some(r => r.reason === 'Overdue follow-up')) return 100;
        if (reasons.some(r => r.reason === 'Never contacted')) return 90;
        const staleReason = reasons.find(r => r.reason.includes('Stale'));
        if (staleReason) {
          const daysMatch = staleReason.reason.match(/(\d+)/);
          const days = daysMatch ? parseInt(daysMatch[1]) : 5;
          return 80 + days;
        }
        if (reasons.some(r => r.reason === 'Late stage')) return 70;
        return 0;
      };
      
      const urgencyA = getUrgencyWeight(a.priorityReasons);
      const urgencyB = getUrgencyWeight(b.priorityReasons);
      if (urgencyB !== urgencyA) {
        return urgencyB - urgencyA;
      }
      
      // TERTIARY: Inaction state (OVERDUE > DUE > FRESH > STALE)
      const inactionA = computeInaction({ lead: a.lead });
      const inactionB = computeInaction({ lead: b.lead });
      const inactionOrder: Record<string, number> = { OVERDUE: 4, DUE: 3, FRESH: 2, STALE: 1 };
      const inactionScoreA = inactionOrder[inactionA.state] || 0;
      const inactionScoreB = inactionOrder[inactionB.state] || 0;
      if (inactionScoreB !== inactionScoreA) {
        return inactionScoreB - inactionScoreA;
      }
      
      const actionableA = (a.contact?.phone || a.contact?.whatsappLink) ? 2 : (a.contact?.email ? 1 : 0);
      const actionableB = (b.contact?.phone || b.contact?.whatsappLink) ? 2 : (b.contact?.email ? 1 : 0);
      if (actionableB !== actionableA) {
        return actionableB - actionableA;
      }
      
      return (b.moneyAtRisk || 0) - (a.moneyAtRisk || 0);
    })
    .slice(0, finalConfig.optionalLimit || 18); // Top N optional actions
  
  // Apply cold lead limit (only for optional)
  const coldActions = optional.filter(a => a.lead.temperature === 'cold');
  const nonColdOptional = optional.filter(a => a.lead.temperature !== 'cold');
  const limitedCold = coldActions.slice(0, finalConfig.maxColdPerDay || 3);
  const finalOptional = [...nonColdOptional, ...limitedCold].sort((a, b) => {
    // Use same DSOS sorting logic
    if (b.priority !== a.priority) {
      return b.priority - a.priority;
    }
    
    const getUrgencyWeight = (reasons: Array<{ reason: string; weight: number }>): number => {
      if (reasons.some(r => r.reason === 'Overdue follow-up')) return 100;
      if (reasons.some(r => r.reason === 'Never contacted')) return 90;
      const staleReason = reasons.find(r => r.reason.includes('Stale'));
      if (staleReason) {
        const daysMatch = staleReason.reason.match(/(\d+)/);
        const days = daysMatch ? parseInt(daysMatch[1]) : 5;
        return 80 + days;
      }
      if (reasons.some(r => r.reason === 'Late stage')) return 70;
      return 0;
    };
    
    const urgencyA = getUrgencyWeight(a.priorityReasons);
    const urgencyB = getUrgencyWeight(b.priorityReasons);
    if (urgencyB !== urgencyA) {
      return urgencyB - urgencyA;
    }
    
    const actionableA = (a.contact?.phone || a.contact?.whatsappLink) ? 2 : (a.contact?.email ? 1 : 0);
    const actionableB = (b.contact?.phone || b.contact?.whatsappLink) ? 2 : (b.contact?.email ? 1 : 0);
    if (actionableB !== actionableA) {
      return actionableB - actionableA;
    }
    
    return (b.moneyAtRisk || 0) - (a.moneyAtRisk || 0);
  });
  
  // finalMandatory already computed above with fallback
  // Limit finalOptional to optionalLimit (already sorted by priority)
  const finalOptionalLimited = finalOptional.slice(0, finalConfig.optionalLimit || 18);
  const backlog = [
    ...mandatory.slice(finalConfig.mandatoryLimit),
    ...finalOptional.slice(finalConfig.optionalLimit)
  ];
  
  // Tomorrow preview (actions scheduled for tomorrow)
  const tomorrowActions = allActions
    .filter(a => {
      const dueDate = startOfDay(a.dueAt);
      return format(dueDate, 'yyyy-MM-dd') === format(tomorrow, 'yyyy-MM-dd');
    })
    .sort((a, b) => {
      // Use DSOS sorting logic (same as mandatory/optional)
      if (b.priority !== a.priority) {
        return b.priority - a.priority;
      }
      
      const getUrgencyWeight = (reasons: Array<{ reason: string; weight: number }>): number => {
        if (reasons.some(r => r.reason === 'Overdue follow-up')) return 100;
        if (reasons.some(r => r.reason === 'Never contacted')) return 90;
        const staleReason = reasons.find(r => r.reason.includes('Stale'));
        if (staleReason) {
          const daysMatch = staleReason.reason.match(/(\d+)/);
          const days = daysMatch ? parseInt(daysMatch[1]) : 5;
          return 80 + days;
        }
        if (reasons.some(r => r.reason === 'Late stage')) return 70;
        return 0;
      };
      
      const urgencyA = getUrgencyWeight(a.priorityReasons);
      const urgencyB = getUrgencyWeight(b.priorityReasons);
      if (urgencyB !== urgencyA) {
        return urgencyB - urgencyA;
      }
      
      const actionableA = (a.contact?.phone || a.contact?.whatsappLink) ? 2 : (a.contact?.email ? 1 : 0);
      const actionableB = (b.contact?.phone || b.contact?.whatsappLink) ? 2 : (b.contact?.email ? 1 : 0);
      if (actionableB !== actionableA) {
        return actionableB - actionableA;
      }
      
      return (b.moneyAtRisk || 0) - (a.moneyAtRisk || 0);
    })
    .slice(0, 5);
  
  // CRITICAL: oneThing = lead with highest priority score
  // Prefer actionable leads (phone/whatsapp) over email-only or no-contact
  // But if highest priority is non-actionable, still show it (with "Complete contact details" action)
  // CRITICAL: If we have active leads, we MUST have oneThing (even if all have priority 0)
  const oneThing = finalMandatory.length > 0 
    ? finalMandatory[0] // First mandatory action (highest priority)
    : (finalOptionalLimited.length > 0 
        ? finalOptionalLimited[0] // Fallback to first optional if no mandatory
        : (allActions.length > 0 ? allActions[0] : null)); // Fallback to first action if no mandatory/optional (ensures oneThing exists if leads exist)
  
  // Calculate execution debt
  const overdueActions = allActions.filter(a => isOverdue(a.dueAt, now));
  const slaViolatedActions = allActions.filter(a => 
    isSLAViolated(a.lead, a.lead.last_interaction_at ? new Date(a.lead.last_interaction_at) : null, now)
  );
  const debtMoney = [...overdueActions, ...slaViolatedActions]
    .reduce((sum, a) => sum + a.moneyAtRisk, 0);
  const debtPoints = overdueActions.length + slaViolatedActions.length;
  
  // Pipeline hygiene
  const missingCloseDates = activeDeals.filter(d => 
    ['proposal_sent', 'negotiation'].includes(d.stage || '') && 
    !d.expected_close_date
  ).length;
  
  const missingNextSteps = activeLeads.filter(l => {
    const stage = l.pipeline_stage || 'new';
    return (stage === 'contacted' || stage === 'appointment') && 
      !(l as any).next_follow_up_at && 
      !l.next_action_at;
  }).length;
  
  // Temperature drift (ALWAYS ENABLED - automatic)
  // Hot → Warm after 2 days without contact
  // Warm → Cold after 7 days without contact
  const hotToWarm = activeLeads.filter(l => {
    const temp = l.temperature || 'cold';
    const lastContact = l.last_interaction_at ? new Date(l.last_interaction_at) : null;
    return temp === 'hot' && lastContact && differenceInHours(now, lastContact) > 48;
  });
  
  const warmToCold = activeLeads.filter(l => {
    const temp = l.temperature || 'cold';
    const lastContact = l.last_interaction_at ? new Date(l.last_interaction_at) : null;
    return temp === 'warm' && lastContact && differenceInDays(now, lastContact) >= 7;
  });
  
  const temperatureDriftCount = hotToWarm.length + warmToCold.length;
  
  return {
    oneThing,
    mandatory: finalMandatory,
    optional: finalOptionalLimited,
    tomorrow: tomorrowActions,
    insights: {
      executionDebt: {
        debtPoints,
        debtMoney,
      },
      dailyCapacity: {
        mandatoryCount: finalMandatory.length,
        optionalCount: finalOptionalLimited.length,
        backlogCount: backlog.length,
      },
      pipelineHygiene: {
        missingCloseDates,
        missingNextSteps,
      },
      temperatureDrift: {
        hotToWarm: temperatureDriftCount,
      },
    },
  };
}

